import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronRight, ArrowUpRight, Building2, MapPin, Users } from "lucide-react";
import { fetcher } from "@/lib/api";
import { fmtInt, fmtMoney, fmtPct, fmtDec, fmtCompact } from "@/lib/format";
import { Breadcrumb, KpiBanner } from "@/components/Shared";
import { MetricCard } from "@/components/MetricCard";
import { TrendChart } from "@/components/TrendChart";
import { PersonAvatar } from "@/components/PersonAvatar";
import { SemaforoBadge, SemaforoDot, TrafficLight } from "@/components/Semaforo";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const META = {
  ventas: { title: "Ventas", fmt: fmtCompact, valueKey: "tendencia_kilos", valueFmt: fmtInt, unit: "kg" },
  adr: { title: "ADR", fmt: (v) => fmtMoney(v), valueKey: "utilidad_por_kilo", valueFmt: (v) => fmtMoney(v), unit: "$/kg" },
  comisiones: { title: "Comisiones y descuentos", fmt: (v) => fmtMoney(v), valueKey: "tendencia_kilos", valueFmt: fmtInt, unit: "kg" },
  inventarios: { title: "Inventarios", fmt: (v) => `${fmtDec(v, 0)}%`, valueKey: "hg_neto_pct", valueFmt: (v) => fmtPct(v), unit: "%" },
  taller: { title: "Taller", fmt: (v) => fmtInt(v), valueKey: "unidades_taller", valueFmt: fmtInt, unit: "u" },
  presupuesto: { title: "Presupuesto planta", fmt: (v) => fmtMoney(v), valueKey: "tendencia_kilos", valueFmt: fmtInt, unit: "kg" },
};

const LEVEL_ICON = { zona: MapPin, regional: Users, empresa: Building2, division: Building2 };

function linkFor(level, id) {
  if (level === "empresa") return `/company/${encodeURIComponent(id)}`;
  if (level === "grupo") return "/";
  return `/${level}/${encodeURIComponent(id)}`;
}

function ChildCard({ child, childLevel, onClick }) {
  const Icon = LEVEL_ICON[childLevel] || Building2;
  return (
    <button
      onClick={onClick}
      data-testid={`child-card-${child.id}`}
      className="group text-left bg-white border border-slate-200 rounded-sm shadow-sm p-4 hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          {childLevel === "empresa" ? (
            <PersonAvatar name={child.gerente} size="sm" />
          ) : (
            <div className="w-8 h-8 rounded-sm bg-[#E2333F]/10 flex items-center justify-center shrink-0">
              <Icon className="w-4 h-4 text-[#E2333F]" />
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="font-display font-bold text-slate-900 text-sm truncate">{child.name}</h3>
              {child.is_real && (
                <span className="text-[8px] font-bold uppercase bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded-sm">real</span>
              )}
            </div>
            {child.gerente && <span className="text-xs text-slate-400 truncate block">{child.gerente}</span>}
          </div>
        </div>
        <SemaforoDot status={child.status} size={11} />
      </div>
      <div className="grid grid-cols-3 gap-2 border-t border-slate-100 pt-3">
        <Mini label="Tendencia" value={`${fmtInt(child.tendencia_kilos)}`} sub="kg" />
        <Mini label="Util." value={fmtMoney(child.utilidad_por_kilo)} sub="$/kg" accent />
        <Mini label="HG" value={fmtPct(child.hg_neto_pct)} sub="" />
      </div>
      <div className="flex items-center justify-between mt-3">
        <span className="text-[11px] text-slate-400">{child.num_empresas} empresas</span>
        <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-[#E2333F] transition-colors" />
      </div>
    </button>
  );
}

function Mini({ label, value, sub, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
      <div className={`font-display font-bold text-sm ${accent ? "text-[#E2333F]" : "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </div>
  );
}

function MetricDialog({ metricKey, data, navigate }) {
  if (!metricKey) return null;
  const meta = META[metricKey];
  const block = data.metrics[metricKey] || {};
  const children = [...(data.children || [])].sort((a, b) => (b[meta.valueKey] || 0) - (a[meta.valueKey] || 0));
  return (
    <>
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mb-5">
        <TrendChart data={block.trend || []} height={240} formatter={meta.fmt} />
      </div>
      <h4 className="font-display font-bold text-slate-800 text-sm mb-2">
        Desglose por {data.child_level}
      </h4>
      <div className="border border-slate-200 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-100 hover:bg-slate-100">
              <TableHead className="text-xs uppercase tracking-wider">Estado</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">{data.child_level}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-right">{meta.title}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-right w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {children.map((c) => (
              <TableRow
                key={c.id}
                className="border-b border-slate-100 cursor-pointer hover:bg-slate-50"
                onClick={() => navigate(linkFor(data.child_level, c.id))}
                data-testid={`metric-child-row-${c.id}`}
              >
                <TableCell><SemaforoDot status={c.status} /></TableCell>
                <TableCell className="font-medium max-w-[260px] truncate">{c.name}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">
                  {meta.valueFmt(c[meta.valueKey])} <span className="text-slate-400 text-xs">{meta.unit}</span>
                </TableCell>
                <TableCell className="text-right"><ChevronRight className="w-4 h-4 text-slate-300 inline" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

export default function NodeDashboard({ level }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(null);
  const { data, isLoading, isError } = useQuery({
    queryKey: [level, id],
    queryFn: () => fetcher(`/${level}/${encodeURIComponent(id)}`),
  });

  if (isLoading) return <div className="text-slate-400 text-sm">Cargando {level}…</div>;
  if (isError || !data) return <div className="text-slate-500 text-sm">No se encontró el nivel solicitado.</div>;

  const t = data.totals;
  const sem = t.semaforo || { status: t.status };
  const order = ["ventas", "adr", "comisiones", "inventarios", "taller", "presupuesto"];

  const crumbs = [
    ...data.breadcrumb.map((b) => ({ label: b.label, to: linkFor(b.level, b.id) })),
    { label: data.name },
  ];

  const levelLabel = { division: "División", zona: "Zona", regional: "Regional" }[level] || level;

  return (
    <div className="zae-fade max-w-[1400px] mx-auto">
      <Breadcrumb items={crumbs} />

      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div className="flex items-center gap-4">
          {data.responsable ? (
            <PersonAvatar name={data.responsable} size="lg" />
          ) : (
            <TrafficLight status={sem.status} />
          )}
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#E2333F] font-bold mb-1">{levelLabel}</div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-slate-900">{data.name}</h1>
              <SemaforoBadge status={sem.status} />
            </div>
            <p className="text-slate-500 text-sm mt-1">
              {data.responsable ? `Responsable: ${data.responsable} · ` : ""}
              {t.num_empresas} empresas · Periodo {data.period}
            </p>
          </div>
        </div>
        <div className="hidden md:flex"><TrafficLight status={sem.status} /></div>
      </div>

      <KpiBanner
        stats={[
          { label: "Tendencia ventas", value: fmtInt(t.tendencia_kilos), sub: "kilos" },
          { label: "Acumulado mes", value: fmtInt(t.acumulado), sub: "kilos" },
          { label: "Vs mes anterior", value: `${fmtDec((sem.momentum || 1) * 100, 0)}%`, sub: "momentum" },
          { label: "Utilidad gestión", value: fmtMoney(t.utilidad_por_kilo), sub: "promedio $/kilo" },
          { label: "HG Neto", value: fmtPct(t.hg_neto_pct), sub: "promedio" },
          { label: "Unidades taller", value: fmtInt(t.unidades_taller), sub: "flota" },
        ]}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
        {order.map((k) => (
          <MetricCard
            key={k}
            title={META[k].title}
            label={data.metrics[k]?.label}
            trend={data.metrics[k]?.trend || []}
            formatter={META[k].fmt}
            onClick={() => setOpen(k)}
            testId={`metric-card-${k}`}
          />
        ))}
      </div>

      <div className="flex items-center gap-2 mb-3">
        <h2 className="font-display font-bold text-lg text-slate-800">
          {{ zona: "Zonas", regional: "Regionales", empresa: "Empresas" }[data.child_level] || "Detalle"}
        </h2>
        <span className="text-sm text-slate-400">— click para entrar al detalle</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {data.children.map((c) => (
          <ChildCard
            key={c.id}
            child={c}
            childLevel={data.child_level}
            onClick={() => navigate(linkFor(data.child_level, c.id))}
          />
        ))}
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-sm" data-testid="metric-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl flex items-center gap-2">
              <span className="bg-[#E2333F] text-white text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm">
                {open && META[open].title}
              </span>
              <span className="text-slate-400 text-sm font-medium">{data.name}</span>
            </DialogTitle>
            <DialogDescription>Detalle agregado y desglose por {data.child_level} · {data.period}</DialogDescription>
          </DialogHeader>
          <MetricDialog metricKey={open} data={data} navigate={(to) => { setOpen(null); navigate(to); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
